import { useEffect, useRef } from 'react';
import { lockAxis, revealOffset, settle } from '../utils/gestures/drag';

/** Where a card swipe is read: the stacked cards, which never scroll sideways (index.css). */
export const CARD_MEDIA = '(max-width: 767px)';
/** The rows a card swipe moves; the page swipe (phase 4) leaves strokes on them alone. */
export const CARD_SELECTOR = '[data-swipe="card"]';
// Where a stroke is never a card swipe: what takes a finger's drag for
// itself, and the tray, whose buttons are pressed, not dragged.
const IGNORE = 'input, textarea, select, [contenteditable="true"], [role="slider"], [data-gesture="off"], [data-card-tray]';
/** What a card swipe uncovers, and is measured: the row's element marked data-card-reveal. */
export const REVEAL_SELECTOR = '[data-card-reveal]';
// Its width when it cannot be measured (jsdom; one not laid out yet): index.css --tray-w.
export const FALLBACK_TRAY_WIDTH = 148;
// How long after a swipe the click it may leave behind is swallowed.
const SWALLOW_MS = 500;
// How much of the stroke's end its speed is read from (ms).
const VELOCITY_WINDOW = 100;

const matches = (query) => {
  try {
    return !!window.matchMedia?.(query)?.matches;
  } catch {
    return false;
  }
};
const now = () => (typeof performance !== 'undefined' && performance.now ? performance.now() : Date.now());

/**
 * A card dragged sideways to show what lies under it (Home's isolate cards;
 * docs/UI_HUBS_AND_GESTURES_PLAN.md section 2): from rest, the "Open in the
 * workbench" hint, and the image source picker when it settles open; from
 * its Lab analytics tray, opened by the card's toggle, the tray, which a
 * stroke back closes. The host says which by marking that element
 * data-card-reveal (REVEAL_SELECTOR), the one this measures.
 *
 * The listeners are delegated: one set on `body` (the table's tbody, an
 * element, not a ref: it comes and goes with the rows), for every row in it
 * marked data-swipe="card" and carrying data-card-id. A stroke counts only:
 *
 *   - from a finger or a pen (pointerType touch or pen), never a mouse;
 *   - while CARD_MEDIA matches: the stacked cards, which have no sideways
 *     scroll of their own; wider, the table scrolls sideways, and a row's
 *     visible toggle opens its tray instead;
 *   - on a row `canReveal(id)` says has something to show, and not starting
 *     in a text field, a slider, anything marked data-gesture="off", or the
 *     tray (data-card-tray).
 *
 * The axis is decided first (drag.js lockAxis): a stroke that goes down is
 * the page's scroll and is let go; only one that goes across takes the
 * pointer (setPointerCapture) and moves the card. While it moves, nothing
 * re-renders: the card's offset is written into the row's --reveal (px) and
 * the row is marked data-revealing (index.css then shows the hint, and drops
 * the easing so the card keeps up with the finger). When the finger lifts,
 * drag.js settle says open or closed, the row's own style is cleared (so a
 * card left closed slides back), and onSettle(id, open, wasOpen) commits it:
 * the host opens the picker for a card pulled open from rest, and closes the
 * tray of one pushed back; the row's class (hub-row--tray) holds a tray open.
 *
 * Direction is PHYSICAL, in Hebrew as in English (decision D12): the finger
 * moving left to right slides the card right, showing what is under it at
 * the card's physical left, and right to left closes it -- the way the camera's edges
 * and ui/Sheet's sides are physical. So this reads clientX as it is, and the
 * card moves by translateX, never by an inline-start offset that would turn
 * round in a right-to-left page.
 *
 * One finger: a second one landing (a pinch, which stays the page's own)
 * drops the stroke and puts the card back. A tap is no stroke: it stays the
 * click it is, so a button on the card still presses. A swipe that began on
 * a button swallows the click the browser may send after it, on that row,
 * once.
 *
 * With prefers-reduced-motion the card does not follow the finger: the
 * stroke is still read, and it settles when the finger lifts, with no slide
 * (index.css drops the transition too).
 */
export default function useCardReveal(body, {
  enabled = true, openId = null, canReveal = () => true, onSettle, media = CARD_MEDIA, clock = now,
} = {}) {
  const latest = useRef(null);
  latest.current = { enabled, openId, canReveal, onSettle, media, clock };

  useEffect(() => {
    if (!body) return undefined;
    let stroke = null;
    let swallow = null;

    const clear = (s) => {
      s.row.style.removeProperty('--reveal');
      s.row.removeAttribute('data-revealing');
      try {
        if (s.captured) s.row.releasePointerCapture?.(s.pointerId);
      } catch { /* already released */ }
    };

    const onDown = (e) => {
      swallow = null;
      if (stroke) {
        // A second finger: a pinch, the page's. The card goes back.
        if (e.pointerId !== stroke.pointerId && stroke.axis === 'x') clear(stroke);
        stroke = null;
        return;
      }
      const now = latest.current;
      if (!now.enabled) return;
      if (e.pointerType !== 'touch' && e.pointerType !== 'pen') return;
      if (!matches(now.media)) return;
      const target = e.target;
      const row = target?.closest?.(CARD_SELECTOR);
      if (!row || !body.contains(row) || target.closest(IGNORE)) return;
      const id = row.getAttribute('data-card-id');
      if (!id || !now.canReveal(id)) return;
      const t = now.clock();
      stroke = {
        id, row, pointerId: e.pointerId, x: e.clientX, y: e.clientY, axis: null, captured: false,
        open: now.openId === id, width: FALLBACK_TRAY_WIDTH, dx: 0, samples: [{ x: e.clientX, t }],
        still: matches('(prefers-reduced-motion: reduce)'),
      };
    };

    const onMove = (e) => {
      const s = stroke;
      if (!s || e.pointerId !== s.pointerId) return;
      const dx = e.clientX - s.x;
      const dy = e.clientY - s.y;
      if (!s.axis) {
        const axis = lockAxis(dx, dy);
        if (axis === 'y') {
          stroke = null; // the page's scroll
          return;
        }
        if (axis !== 'x') return;
        s.axis = 'x';
        try {
          s.row.setPointerCapture?.(e.pointerId);
          s.captured = true;
        } catch { /* a synthetic pointer */ }
        if (!s.still) {
          // Shown first, so it can be measured: hidden it has no width.
          s.row.setAttribute('data-revealing', '');
          const under = s.row.querySelector(REVEAL_SELECTOR);
          const inner = under?.firstElementChild;
          const measured = (inner || under)?.getBoundingClientRect?.().width;
          if (measured > 0) s.width = measured;
        }
      }
      s.dx = dx;
      const t = latest.current.clock();
      s.samples.push({ x: e.clientX, t });
      while (s.samples.length > 2 && t - s.samples[0].t > VELOCITY_WINDOW) s.samples.shift();
      if (!s.still) s.row.style.setProperty('--reveal', `${revealOffset(dx, s.width, s.open)}px`);
    };

    const onUp = (e) => {
      const s = stroke;
      if (!s || e.pointerId !== s.pointerId) return;
      stroke = null;
      if (s.axis !== 'x') return; // a tap: the click is the button's
      const t = latest.current.clock();
      const first = s.samples[0];
      const dx = e.clientX - s.x;
      const span = t - first.t;
      const vx = span > 0 ? (e.clientX - first.x) / span : 0;
      clear(s);
      swallow = { row: s.row, until: Date.now() + SWALLOW_MS };
      const open = settle({ dx, vx, trayWidth: s.width, open: s.open });
      latest.current.onSettle?.(s.id, open, s.open);
    };

    const onCancel = (e) => {
      const s = stroke;
      if (!s || e.pointerId !== s.pointerId) return;
      stroke = null;
      if (s.axis === 'x') clear(s);
    };

    const onClick = (e) => {
      const held = swallow;
      if (!held) return;
      swallow = null;
      if (Date.now() > held.until || !held.row.contains(e.target)) return;
      e.preventDefault();
      e.stopPropagation();
    };

    body.addEventListener('pointerdown', onDown);
    body.addEventListener('pointermove', onMove);
    body.addEventListener('pointerup', onUp);
    body.addEventListener('pointercancel', onCancel);
    body.addEventListener('click', onClick, true);
    return () => {
      if (stroke?.axis === 'x') clear(stroke);
      stroke = null;
      body.removeEventListener('pointerdown', onDown);
      body.removeEventListener('pointermove', onMove);
      body.removeEventListener('pointerup', onUp);
      body.removeEventListener('pointercancel', onCancel);
      body.removeEventListener('click', onClick, true);
    };
  }, [body]);
}
